import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Product } from '../../Entities/Product.ts';
import SystemDataService from '../../Services/SystemDataService.js';
import MediaComponent from '../Media/media-component.js';
import ButtonCard from './button-card-component.js';
import ContentSection from '../Content/content-section-component.js';
import ProductStarRating from './product-star-rating-component.js';
import ProductCTASection from './product-cta-component.js';
import MobileProductCTABar from './mobile-product-cta-bar-component.js';
import { getProductPageData } from '../../Public/product-page-data.js';
import useHandleRedirect from '../../Hooks/useHandleRedirect.js';
import InfoBarSection from '../Sections/section-info-bar-component.js';
import { ShareIcon } from '../Icons/icons-component.js';

const EASE = [0.22, 1, 0.36, 1];

/**
 * ProductCard
 * The single source of truth for all product detail UI.
 *
 * Props:
 *   productData, raw product object from Redux / API
 *   stats, optional { soldCount } from ProductActivityService
 *   onAddSuccess, optional callback fired after a successful Add to Cart
 *   onImageClick, optional callback(src) when the main image is clicked
 *   showMobileBar, set false when rendered inside a modal (default true)
 */
const ProductCard = ({ productData, stats, onAddSuccess, onImageClick, showMobileBar = true }) => {
    const navigate = useHandleRedirect();
    const { dir = 'ltr', links = [], language = 'en' } = useSelector((s) => s.systemData || {});
    const txt = useMemo(() => getProductPageData(language), [language]);

    const [activeMedia, setActiveMedia] = useState(null);
    const [registry, setRegistry] = useState([]);
    const [selectedVariant, setSelectedVariant] = useState(null);

    const heroRef = useRef(null);
    const attrRef = useRef(null);
    const heroInView = useInView(heroRef, { once: true, amount: 0.15 });
    const attrInView = useInView(attrRef, { once: true, amount: 0.2 });

    const product = useMemo(() => Product.fromObject(productData), [productData]);

    const variants    = useMemo(() => product.variants || [], [product]);
    const hasVariants = variants.length > 0;

    // Derive initial variant synchronously so there's never a null flash
    const initialVariant = useMemo(() => {
        if (!hasVariants) return null;
        return variants.find(v => v.stockOnHand > 0) || variants[0] || null;
    }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSelectedVariant(initialVariant);
    }, [initialVariant]);

    const gallery = useMemo(() => [product.image, ...(Array.isArray(product.images) ? product.images : [])].filter(Boolean), [product]);

    const activeVariant  = selectedVariant ?? initialVariant;
    const isOutOfStock   = hasVariants
        ? (!activeVariant || activeVariant.stockOnHand <= 0)
        : product.inventory?.stockOnHand <= 0;
    const maxQty = hasVariants
        ? (activeVariant?.stockOnHand ?? 0)
        : (product.inventory?.stockOnHand ?? Infinity);
    const displayPrice = hasVariants && activeVariant
        ? Number(activeVariant.price).toFixed(2)
        : product.finalPrice?.toFixed(2);

    // Spec subtitle: prefer wire/inner diameter + coil length, then SKU
    const activeSku = (hasVariants && activeVariant?.sku) ? activeVariant.sku : product.sku;
    const dimensionSubtitle = [product.wireDiameter, product.innerDiameter, product.coilLength].filter(Boolean).join(' / ') || activeSku || null;

    // Social + support links
    const facebookLink = SystemDataService.byLabel(links, 'Facebook');
    const twitterLink = SystemDataService.byLabel(links, 'Twitter') || SystemDataService.byLabel(links, 'X');
    const instagramLink = SystemDataService.byLabel(links, 'Instagram');
    const whatsapp = SystemDataService.byLabel(links, 'WhatsApp');

    // Payment logos
    const stripeLogo = SystemDataService.byLabel(links, 'Stripe Logo');
    const mastercardLogo = SystemDataService.byLabel(links, 'Mastercard Logo');
    const applePayLogo = SystemDataService.byLabel(links, 'ApplePay Logo');
    const googlePayLogo = SystemDataService.byLabel(links, 'GooglePay Logo');
    const paypalLogo = SystemDataService.byLabel(links, 'Paypal Logo');

    useEffect(() => {
        setActiveMedia(initialVariant?.image || product.image);
        SystemDataService.getProductAttributes().then(data => setRegistry(data || []));
    }, [productData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Stagger delays for right-column elements
    const d = (n) => ({ delay: n, duration: 0.5, ease: EASE });

    return (
        <>
            {/* ── MAIN GRID ── */}
            <div
                ref={heroRef}
                className="max-w-[1500px] mx-auto px-4 py-12 lg:py-20 pb-28 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20"
            >
                {/* ─── LEFT: Sticky Media Hub ─────────────────────────────── */}
                {/* Plain div owns sticky, no transform here that could break sticky */}
                <div className="lg:col-span-7 lg:sticky lg:top-24 self-start">
                <motion.div
                    className="flex flex-col md:flex-row gap-8"
                    initial={{ opacity: 0, x: -40 }}
                    animate={heroInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    {/* Thumbnails */}
                    <div className="flex md:flex-col gap-4 order-2 md:order-1 overflow-x-auto scrollbar-hide">
                        {gallery.map((img, i) => (
                            <motion.button
                                key={i}
                                onClick={() => setActiveMedia(img)}
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={heroInView ? { opacity: 1, scale: 1 } : {}}
                                transition={{ delay: 0.15 + i * 0.07, duration: 0.45, ease: EASE }}
                                className={`relative w-24 h-24 rounded-[30px] border-2 transition-all overflow-hidden bg-white shadow-sm ${activeMedia === img ? 'border-secondary-500' : 'border-transparent opacity-60'}`}
                            >
                                <MediaComponent
                                    media={img} alt="Gallery View" objectFit="contain"
                                    className="mix-blend-multiply"
                                    wrapperClassName="absolute inset-0 w-full h-full flex items-center justify-center p-2"
                                    quality={50}
                                />
                            </motion.button>
                        ))}
                    </div>

                    {/* Main image stage */}
                    <div className="flex-1 aspect-square relative order-1 md:order-2">
                        <div
                            className={`w-full h-full bg-gray-50 rounded-[60px] relative overflow-hidden ${onImageClick ? 'cursor-zoom-in' : ''}`}
                            onClick={onImageClick ? () => onImageClick(activeMedia) : undefined}
                        >
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeMedia}
                                    className="absolute inset-0"
                                    initial={{ opacity: 0, scale: 1.04 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: 0.35, ease: EASE }}
                                >
                                    <MediaComponent
                                        media={activeMedia} objectFit="contain"
                                        wrapperClassName="absolute inset-0 w-full h-full flex items-center justify-center p-16"
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Corner badges */}
                        <AnimatePresence>
                            {isOutOfStock && (
                                <motion.div key="sold-out" className="absolute top-0 right-0 z-10"
                                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
                                    <span className="bg-primary-500 text-white text-xs font-bold px-5 py-3 rounded-bl-2xl rounded-tr-[60px] uppercase tracking-[0.2em] italic block">{txt.soldOut}</span>
                                </motion.div>
                            )}
                            {!isOutOfStock && (stats?.soldCount ?? 0) <= 10 && product.inventory?.stockOnHand > 0 && product.inventory?.stockOnHand < 30 && (
                                <motion.div key="few-left" className="absolute top-0 right-0 z-10"
                                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
                                    <span className="bg-secondary-500 text-white text-xs font-bold px-5 py-3 rounded-bl-2xl rounded-tr-[60px] uppercase tracking-[0.15em] italic block">{txt.onlyFewLeft}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
                </div>

                {/* ─── RIGHT: Purchase Column ──────────────────────────────── */}
                <motion.div
                    className="lg:col-span-5 flex flex-col gap-6"
                    initial={{ opacity: 0, x: 40 }}
                    animate={heroInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                >
                    {/* ── Breadcrumb ── */}
                    <motion.nav
                        initial={{ opacity: 0, y: 6 }} animate={heroInView ? { opacity: 1, y: 0 } : {}}
                        transition={d(0.28)}
                        className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400"
                    >
                        <button onClick={() => navigate('/')} className="hover:text-secondary-500 transition-colors">{txt.metaHome}</button>
                        {product.category?.name && (
                            <>
                                <span className="opacity-40">/</span>
                                <button
                                    onClick={() => navigate(`/categories/${product.category.name}`)}
                                    className="hover:text-secondary-500 transition-colors"
                                >
                                    {product.category.name}
                                </button>
                            </>
                        )}
                    </motion.nav>

                    {/* ── White purchase card ── */}
                    <section className="bg-white rounded-[50px] p-6 border border-gray-100 shadow-2xl shadow-gray-200/20 relative overflow-hidden group/card">
                        <div className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-secondary-500/5 via-transparent to-transparent pointer-events-none" />

                        {/* Stars + single share button */}
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.35)}
                            className="flex items-center justify-between gap-3 mb-5">
                            <div className="flex items-center gap-3">
                                <ProductStarRating rating={5.0} />
                            </div>
                            <SingleShareButton txt={txt} />
                        </motion.div>

                        {/* Title */}
                        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.42)}
                            className="text-4xl lg:text-5xl font-bold text-primary-950 leading-none mb-2 uppercase er italic">
                            {product.name}
                        </motion.h1>

                        {/* SKU / dimensions subtitle */}
                        {dimensionSubtitle && (
                            <motion.p initial={{ opacity: 0, y: 8 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.45)}
                                className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">
                                {dimensionSubtitle}
                            </motion.p>
                        )}

                        {/* Price */}
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.48)}
                            className="flex items-center gap-4 mb-4">
                            <span className="text-4xl font-bold text-secondary-500">${displayPrice}</span>
                            {product.isDiscountPrice && (
                                <span className="text-xl font-bold text-gray-300 line-through">${product.originalPrice?.toFixed(2)}</span>
                            )}
                        </motion.div>

                        {/* In-stock indicator */}
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.51)}
                            className="flex items-center gap-2 mb-8">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${isOutOfStock ? 'bg-red-400' : 'bg-emerald-400'}`} />
                            <span className="text-xs font-bold text-gray-500">
                                {isOutOfStock ? txt.outOfStock : txt.inStock}
                            </span>
                        </motion.div>

                        {/* ── Side Variant Selector ── */}
                        {hasVariants && (
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.54)}
                                className="mb-6">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">{txt.selectSide}</p>
                                <div className="flex gap-2 flex-wrap">
                                    {variants.map(v => {
                                        const isSelected = activeVariant?.id === v.id;
                                        const noStock    = v.stockOnHand <= 0;
                                        const isLeft     = v.side === 'left';
                                        return (
                                            <button
                                                key={v.id}
                                                onClick={() => {
                                                    if (noStock) return;
                                                    setSelectedVariant(v);
                                                    if (v.image) setActiveMedia(v.image);
                                                }}
                                                disabled={noStock}
                                                className={[
                                                    'flex items-center gap-2 px-5 py-3 rounded-2xl border-2 font-bold text-xs uppercase tracking-widest transition-all',
                                                    isSelected ? 'bg-primary-950 text-white border-primary-950' : 'bg-white text-primary-950 border-gray-200 hover:border-primary-950',
                                                    noStock ? 'opacity-40 cursor-not-allowed line-through' : 'cursor-pointer',
                                                ].join(' ')}
                                            >
                                                <span className={`w-3 h-3 rounded-full shrink-0 ${isLeft ? 'bg-red-500' : 'bg-gray-900'} ${noStock ? 'opacity-40' : ''}`} />
                                                {isLeft ? txt.sideLeft : txt.sideRight}
                                                {noStock && ` · ${txt.sideOutOfStock}`}
                                            </button>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {/* ── CTA (buttons unchanged) ── */}
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.57)}>
                            <ProductCTASection
                                productData={productData}
                                selectedVariant={activeVariant}
                                isOutOfStock={isOutOfStock}
                                maxQty={maxQty}
                                txt={txt}
                                onAddSuccess={onAddSuccess}
                            />
                        </motion.div>

                        {/* Social proof pill */}
                        <AnimatePresence>
                            {!isOutOfStock && stats?.soldCount > 10 && (
                                <motion.div key="sold-badge"
                                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                                    transition={{ duration: 0.4, ease: EASE }} className="flex flex-wrap gap-2 mt-4">
                                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                                        {stats.soldCount} {txt.soldLastFewHours}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* ── Trust badges bar ── */}
                        <motion.div initial={{ opacity: 0 }} animate={heroInView ? { opacity: 1 } : {}} transition={d(0.65)}>
                            <InfoBarSection alwaysScroll />
                        </motion.div>

                        {/* ── Description (below CTA + trust badges) ── */}
                        {product.description && (
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.7)}
                                className="mt-6 pt-6 border-t border-gray-50">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 mb-2">{txt.descriptionTitle}</p>
                                <p className="text-gray-500 text-sm leading-relaxed">{product.description}</p>
                            </motion.div>
                        )}


                        {/* ── Payment logos + social links ── */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={d(0.78)}
                            className="px-4 py-8 bg-white rounded-[40px] flex flex-col gap-6 shadow-xl">
                            <span className="text-[10px] text-primary-950 uppercase tracking-widest opacity-40 text-center">{txt.securePayment}</span>
                            <div className="flex justify-center items-center flex-wrap gap-x-4 gap-y-4">
                                {stripeLogo && <img src={stripeLogo.image} className="h-4 w-auto object-contain" alt="Stripe" />}
                                {mastercardLogo && <img src={mastercardLogo.image} className="h-6 w-auto object-contain" alt="Mastercard" />}
                                {applePayLogo && <img src={applePayLogo.image} className="h-5 w-auto object-contain" alt="ApplePay" />}
                                {googlePayLogo && <img src={googlePayLogo.image} className="h-5 w-auto object-contain" alt="GooglePay" />}
                                {paypalLogo && <img src={paypalLogo.image} className="h-5 w-auto object-contain" alt="Paypal" />}
                            </div>
                            {/* Social links below payment logos */}
                            {(facebookLink || twitterLink || instagramLink) && (
                                <div className="flex justify-center gap-3 pt-2 border-t border-gray-50">
                                    {facebookLink && (
                                        <button onClick={() => window.open(facebookLink.url, '_blank')} className="w-7 h-7 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 p-1 ">
                                            <img src={facebookLink.image} className="w-full h-full object-contain" alt="FB" />
                                        </button>
                                    )}
                                    {twitterLink && (
                                        <button onClick={() => window.open(twitterLink.url, '_blank')} className="w-7 h-7 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 p-1 ">
                                            <img src={twitterLink.image} className="w-full h-full object-contain" alt="TW" />
                                        </button>
                                    )}
                                    {instagramLink && (
                                        <button onClick={() => window.open(instagramLink.url, '_blank')} className="w-7 h-7 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 p-1 ">
                                            <img src={instagramLink.image} className="w-full h-full object-contain" alt="IG" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </motion.div>


                        {/* ── Meta grid ──
                        <motion.div initial={{ opacity: 0 }} animate={heroInView ? { opacity: 1 } : {}} transition={d(0.75)}
                            className="mt-8 pt-6 border-t border-gray-50 grid grid-cols-2 gap-y-4">
                            <MetaField label={txt.metaCategory} value={product.category?.name} />
                            <MetaField label={txt.metaBrand} value={product.brand || 'BH-Industrial'} />
                            {product.inventory?.weight && (
                                <MetaField label={txt.weightLabel} value={product.inventory.weight} />
                            )}
                            {product.specifications?.dimensions && (
                                <MetaField label={txt.dimensionsLabel} value={product.specifications.dimensions} />
                            )}
                        </motion.div> */}
                    </section>


                </motion.div>
            </div>

            {/* ── Technical Attribute Matrix ── */}
            {registry.some(attr => product[attr.key] || product.specifications?.[attr.key]) && (
                <section ref={attrRef} className="max-w-[1500px] mx-auto px-6 py-10 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {registry.map((attr, i) => {
                            const val = product[attr.key] || product.specifications?.[attr.key];
                            if (!val) return null;
                            return (
                                <motion.div key={attr.id}
                                    initial={{ opacity: 0, y: 24 }} animate={attrInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ delay: i * 0.08, duration: 0.5, ease: EASE }}>
                                    <ButtonCard icon={attr.image} label={attr.label} value={val} description={attr.description}
                                        className="!rounded-[45px] !border-none bg-white shadow-xl shadow-gray-200/40 p-10" />
                                </motion.div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── Long Description ── */}
            {/* {product.longDescription && (
                <section className="w-full bg-gray-50/50">
                    <ContentSection
                        title={txt.longDescTitle}
                        subtitle={product.longDescription}
                        buttonText={txt.longDescBtn}
                        onButtonClick={() => window.open(whatsapp?.url, '_blank')}
                        className="!bg-transparent"
                    />
                </section>
            )} */}

            {/* ── Mobile floating CTA bar ── */}
            {showMobileBar && (
                <MobileProductCTABar
                    productData={productData}
                    selectedVariant={activeVariant}
                    price={displayPrice}
                    currencySymbol="$"
                    isOutOfStock={isOutOfStock}
                    txt={txt}
                    onAddSuccess={onAddSuccess}
                />
            )}
        </>
    );
};

// ─── Pure UI helpers ───────────────────────────────────────────────────────────

const MetaField = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">{label}</span>
        <div className="text-xs font-bold text-primary-950 uppercase truncate max-w-[160px]">{value}</div>
    </div>
);

const SingleShareButton = ({ txt }) => {
    const [copied, setCopied] = useState(false);
    const handleShare = useCallback(() => {
        if (navigator.share) {
            navigator.share({ url: window.location.href }).catch(() => {});
        } else {
            navigator.clipboard.writeText(window.location.href).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
            });
        }
    }, []);
    return (
        <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-100 text-gray-400 hover:text-primary-950 hover:border-gray-200 transition-all text-[10px] font-bold uppercase tracking-widest"
        >
            <ShareIcon />
            {copied ? 'Copied!' : txt.metaShare}
        </button>
    );
};

export default ProductCard;
